import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getFatherMembers, addMemberFollowup } from '../../services/api';
import { FiUsers, FiHeart, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Toast from '../../components/Toast';
import PhoneLink from '../../components/PhoneLink';

const statusPill = { member: 'pill-green', inactive: 'pill-red', transferred: 'pill-blue', changed_religion: 'pill-gray', deceased: 'pill-gray', suspended: 'pill-red' };
const followupColor = { good: 'pill-green', needs_attention: 'pill-yellow', critical: 'pill-red', unknown: 'pill-gray' };

const MyMembers = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [followupModal, setFollowupModal] = useState(null);
  const [fup, setFup] = useState({ date: new Date().toISOString().slice(0, 10), notes: '', status: 'unknown', next_followup_date: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const today = new Date().toISOString().slice(0, 10);

  const load = () => {
    if (!user?.member_id) return;
    getFatherMembers(user.member_id).then(r => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };

  useEffect(() => { load(); }, [user]);

  const handleAddFollowup = async () => {
    if (!followupModal) return;
    setSaving(true);
    try {
      await addMemberFollowup(followupModal.id, fup);
      showToast(t('myMembers.followupRecorded'));
      setFollowupModal(null);
      setFup({ date: new Date().toISOString().slice(0, 10), notes: '', status: 'unknown', next_followup_date: '' });
      load();
    } catch (e) { showToast(t('myProfile.failed'), 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.myMembers')}</h1><p>{members.length}</p></div>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('members.membershipCard')}</th>
              <th>{t('common.name')}</th>
              <th>{t('common.phone')}</th>
              <th>{t('common.status')}</th>
              <th>{t('followup.lastFollowup')}</th>
              <th>{t('followup.nextFollowup')}</th>
              <th>{t('followup.title')}</th>
              <th>{t('members.dependents')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr><td colSpan={9}><div className="empty-state"><div className="empty-icon"><FiUsers /></div><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : members.map((m, i) => {
              const isOverdue = m.next_followup && m.next_followup < today;
              return (
                <tr key={m.id} style={{ background: isOverdue ? '#fff1f2' : 'transparent' }}>
                  <td data-label="#">{i + 1}</td>
                  <td data-label={t('members.membershipCard')}><span className="pill pill-gray" style={{ fontFamily: 'monospace', fontSize: 11 }}>{m.card_number || '—'}</span></td>
                  <td data-label={t('common.name')}><strong>{m.title ? `${m.title} ` : ''}{m.first_name} {m.middle_name || ''} {m.last_name}</strong></td>
                  <td data-label={t('common.phone')}><PhoneLink phone={m.phone} whatsapp={m.whatsapp} /></td>
                  <td data-label={t('common.status')}><span className={`pill ${statusPill[m.membership_status] || 'pill-gray'}`}>{t(`status.${m.membership_status}`, m.membership_status)}</span></td>
                  <td data-label={t('followup.lastFollowup')}>{m.last_followup || <span style={{ color: '#9ca3af' }}>{t('myMembers.never')}</span>}</td>
                  <td data-label={t('followup.nextFollowup')} style={{ color: isOverdue ? '#dc2626' : 'inherit', fontWeight: isOverdue ? 700 : 'normal' }}>
                    {m.next_followup || '—'}
                    {isOverdue && <span className="pill pill-red" style={{ marginLeft: 4, fontSize: 10 }}>{t('followup.overdue')}</span>}
                  </td>
                  <td data-label={t('followup.title')}>
                    {m.last_status
                      ? <span className={`pill ${followupColor[m.last_status] || 'pill-gray'}`}>{t(`followup.${m.last_status === 'needs_attention' ? 'needsAttention' : m.last_status}`, m.last_status.replace('_', ' '))}</span>
                      : <span style={{ color: '#9ca3af', fontSize: 12 }}>{t('myMembers.noRecord')}</span>}
                  </td>
                  <td data-label={t('members.dependents')}>
                    {m.dependent_count > 0
                      ? <span className="pill pill-purple">{t('myMembers.dependentCount', { count: m.dependent_count })}</span>
                      : <span style={{ color: '#9ca3af', fontSize: 12 }}>{t('myMembers.none')}</span>}
                  </td>
                  <td data-label={t('common.actions')}>
                    <div className="action-btns">
                      <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/member/member-profile/${m.id}`)}>{t('members.profile')}</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => {
                        setFollowupModal(m);
                        setFup({ date: new Date().toISOString().slice(0, 10), notes: '', status: 'unknown', next_followup_date: '' });
                      }}>
                        <FiHeart /> {t('followup.title')}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Follow-up Modal */}
      {followupModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div>
                <h3>{t('myMembers.recordFollowup')}</h3>
                <p>{followupModal.title ? `${followupModal.title} ` : ''}{followupModal.first_name} {followupModal.last_name}</p>
              </div>
              <button className="btn-icon" onClick={() => setFollowupModal(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>{t('common.date')}</label><input type="date" value={fup.date} onChange={e => setFup(p => ({ ...p, date: e.target.value }))} /></div>
                <div className="form-group"><label>{t('common.status')}</label>
                  <select value={fup.status} onChange={e => setFup(p => ({ ...p, status: e.target.value }))}>
                    <option value="unknown">{t('followup.unknown')}</option>
                    <option value="good">{t('followup.good')}</option>
                    <option value="needs_attention">{t('followup.needsAttention')}</option>
                    <option value="critical">{t('followup.critical')}</option>
                  </select>
                </div>
                <div className="form-group form-full"><label>{t('myMembers.nextFollowupDate')}</label><input type="date" value={fup.next_followup_date} onChange={e => setFup(p => ({ ...p, next_followup_date: e.target.value }))} /></div>
                <div className="form-group form-full"><label>{t('common.notes')}</label><textarea value={fup.notes} onChange={e => setFup(p => ({ ...p, notes: e.target.value }))} rows={3} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setFollowupModal(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleAddFollowup} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default MyMembers;
